import { Provider } from 'types';
import { Firebase } from './../types';

type Credentials = {
  mail: string,
  password: string,
};

const ERRORS_MAP: Record<string, string> = {
  'auth/invalid-email': 'Invalid email',
  'auth/user-disabled': 'User account is disabled',
  'auth/user-not-found': 'User not found',
  'auth/wrong-password': 'Password doesn\'t match the email',
  'auth/email-already-in-use': 'This email is already in use',
  'auth/operation-not-allowed': 'Unknown error occured',
  'auth/weak-password': 'Your password is too weak',
};

class FirebaseMailAuth {
  private _onLoggedIn: (
    user: firebase.User | null,
    additionalUserInfo: firebase.auth.AdditionalUserInfo | null | undefined,
  ) => void;
  private firebase: Firebase;

  constructor(firebase: Firebase, handler: typeof FirebaseMailAuth['prototype']['_onLoggedIn']) {
    this.firebase = firebase;
    this._onLoggedIn = handler;
  }

  public async login({ mail, password }: Credentials) {
    try {
      const { user, additionalUserInfo } = await this.firebase.auth().signInWithEmailAndPassword(mail, password);
      this.notify(user, additionalUserInfo);
    } catch (error) {
      this.handleError(error);
    }
  }
  public async signup({ mail, password }: Credentials) {
    try {
      const { user, additionalUserInfo } = await this.firebase.auth().createUserWithEmailAndPassword(mail, password);
      this.notify(user, additionalUserInfo);
    } catch (error) {
      this.handleError(error);
    }
  }
  public async sendResetEmail(mail: string) {
    return this.firebase.auth().sendPasswordResetEmail(mail).catch(this.handleError);
  }
  // @ts-ignore
  // eslint-disable-next-line consistent-return
  public async checkProviders(mail: string): Promise<Provider> {
    try {
      const result = await this.firebase.auth().fetchSignInMethodsForEmail(mail);
      if (!result.length) {
        return 'signup';
      }

      const [provider] = result;
      return provider as Provider;
    } catch (error) {
      this.handleError(error);
    }
  }

  private handleError = (error: firebase.FirebaseError) => {
    const { code } = error;
    const errorText = this.getErrorText(code);
    throw new Error(errorText);
  }
  private getErrorText(code: string) {
    if (!ERRORS_MAP.hasOwnProperty(code)) {
      return 'Unknown error occured!';
    }

    return ERRORS_MAP[code];
  }
  private notify(user: firebase.User | null, additionalUserInfo: firebase.auth.AdditionalUserInfo | null | undefined) {
    if (typeof this._onLoggedIn !== 'function') return;
    this._onLoggedIn(user, additionalUserInfo);
  }
}

export default FirebaseMailAuth;
